/**
 * Application shell.
 *
 * Navigation is filtered by permission, but that is presentation only — every endpoint enforces
 * the same check server-side. A user who forges their session in devtools reaches a 403, not data.
 */
import { Suspense, type ReactNode } from 'react';

import { StaleBuild } from './components/StaleBuild';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';

import { AuthProvider, useAuth } from './lib/auth';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import RfidDashboard from './pages/RfidDashboard';
import StockCount from './pages/StockCount';
import EpcCounts from './pages/EpcCounts';
import QualityInventory from './pages/QualityInventory';
import Receive from './pages/Receive';
import Lorry from './pages/Lorry';
import RfidOrders from './pages/RfidOrders';
import RfidPostings from './pages/RfidPostings';
import SkuMapping from './pages/SkuMapping';
import { BiOverview, BiSales, BiProducts, BiPurchasing, BiInventory } from './pages/Bi';
import InventorySearch from './pages/InventorySearch';
import { AdminUsers, AdminRoles, AdminAudit } from './pages/Admin';
import SystemHealth from './pages/SystemHealth';

type NavItem = { to: string; label: string; perms: string[] };

/**
 * Exported so a test can assert the nav config itself, not just React Router's behaviour.
 */
export const SECTIONS: Array<{ heading: string; items: NavItem[] }> = [
  {
    heading: 'Business intelligence',
    items: [
      { to: '/bi', label: 'Overview', perms: ['bi.sales.read', 'bi.purchasing.read'] },
      { to: '/bi/sales', label: 'Sales & outlets', perms: ['bi.sales.read'] },
      { to: '/bi/products', label: 'Products', perms: ['bi.product.read'] },
      { to: '/bi/inventory', label: 'Inventory by location', perms: ['bi.inventory.read'] },
      {
        to: '/bi/inventory/search',
        label: 'Find a SKU · quantity',
        perms: ['bi.inventory.read'],
      },
      { to: '/bi/purchasing', label: 'Purchasing', perms: ['bi.purchasing.read'] },
      { to: '/bi/sku-mapping', label: 'SKU mapping', perms: ['bi.skumap.read'] },
    ],
  },
  {
    heading: 'RFID operations',
    items: [
      { to: '/rfid', label: 'Dashboard', perms: ['rfid.dashboard.read'] },
      { to: '/rfid/receive', label: 'Receive against a PO', perms: ['rfid.order.create'] },
      { to: '/rfid/lorry', label: 'Load a lorry', perms: ['rfid.order.create'] },
      { to: '/rfid/orders', label: 'Inbound / outbound orders', perms: ['rfid.dashboard.read'] },
      { to: '/rfid/postings', label: 'Documents from scans', perms: ['rfid.dashboard.read'] },
      {
        to: '/rfid/stock-count',
        label: 'Stock count & variance',
        perms: ['rfid.dashboard.read', 'bi.inventory.read'],
      },
      { to: '/rfid/epc-counts', label: 'EPC stock counts (app)', perms: ['rfid.epc.read'] },
      { to: '/rfid/quality', label: 'Quality by location', perms: ['rfid.epc.read'] },
    ],
  },
  {
    heading: 'Administration',
    items: [
      { to: '/admin/users', label: 'Users', perms: ['admin.user.read'] },
      { to: '/admin/roles', label: 'Roles & permissions', perms: ['admin.role.read'] },
      { to: '/admin/audit', label: 'Audit log', perms: ['admin.audit.read'] },
      // Gated on the JOB permission rather than an admin one: the people who need to know the
      // sync has stopped are the operators watching the order book, not only administrators.
      { to: '/admin/health', label: 'System health', perms: ['rfid.job.read'] },
    ],
  },
];

function Guarded({ perms, children }: { perms: string[]; children: ReactNode }) {
  const { can } = useAuth();
  const location = useLocation();
  if (!can(...perms)) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
        <p className="text-sm font-medium text-slate-900">You do not have access to this page.</p>
        <p className="mt-1 text-xs text-slate-500">
          {location.pathname} requires {perms.join(' or ')}. Ask an administrator if you need it.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

function Shell() {
  const { user, can, logout } = useAuth();
  const sections = SECTIONS.map((s) => ({
    ...s,
    items: s.items.filter((i) => can(...i.perms)),
  })).filter((s) => s.items.length > 0);

  // Land wherever the user actually has access, rather than a page they will be refused.
  const home = sections[0]?.items[0]?.to ?? '/account';

  return (
    <div className="min-h-screen bg-brand-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-4 px-6 py-3">
          <span className="text-sm font-semibold">LSK · RFID &amp; BI</span>
          <div className="ml-auto flex items-center gap-3 text-xs text-slate-600">
            <span>
              {user?.displayName}
              {user?.roles.length ? (
                <span className="ml-1 text-slate-400">({user.roles.join(', ')})</span>
              ) : null}
            </span>
            {user?.locationScope.length ? (
              <span className="rounded bg-amber-50 px-2 py-0.5 text-amber-800">
                scoped to {user.locationScope.join(', ')}
              </span>
            ) : null}
            <NavLink
              to="/account"
              className="rounded border border-slate-300 px-2 py-1 hover:bg-brand-50"
            >
              Account
            </NavLink>
            <button
              onClick={() => void logout()}
              className="rounded border border-slate-300 px-2 py-1 hover:bg-brand-50"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {user?.mustChangePassword && (
        <div className="border-b border-amber-200 bg-amber-50 px-6 py-2 text-sm text-amber-900">
          An administrator has asked you to change your password.{' '}
          <NavLink to="/account" className="font-medium underline">
            Change it now
          </NavLink>
          .
        </div>
      )}

      <div className="flex flex-col gap-6 px-6 py-6 lg:flex-row">
        {/* Pinned on wide screens: the menu stays put while long pages scroll (LSK, 2026-10-04).
            Its own scroll if it ever outgrows the window; stacked above the content on phones. */}
        <nav className="lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:w-56 lg:shrink-0 lg:self-start lg:overflow-y-auto">
          {sections.map((s) => (
            <div key={s.heading} className="mb-5">
              <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {s.heading}
              </h2>
              <ul className="space-y-0.5">
                {s.items.map((i) => (
                  <li key={i.to}>
                    <NavLink
                      to={i.to}
                      // `end` on EVERY item. NavLink matches by PREFIX otherwise, so `/rfid` also
                      // lit up while on `/rfid/stock-count` — two highlighted items at once.
                      // Every entry here is a leaf route, so exact matching is always what we want.
                      end
                      className={({ isActive }) =>
                        `block rounded px-2 py-1.5 text-sm ${
                          isActive ? 'bg-brand-600 text-white' : 'text-slate-700 hover:bg-brand-100'
                        }`
                      }
                    >
                      {i.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <main className="min-w-0 flex-1">
          <Suspense fallback={<p className="text-sm text-slate-400">Loading…</p>}>
            <Routes>
              <Route path="/" element={<Navigate to={home} replace />} />
              <Route
                path="/bi"
                element={
                  <Guarded perms={['bi.sales.read', 'bi.purchasing.read']}>
                    <BiOverview />
                  </Guarded>
                }
              />
              <Route
                path="/bi/sales"
                element={
                  <Guarded perms={['bi.sales.read']}>
                    <BiSales />
                  </Guarded>
                }
              />
              <Route
                path="/bi/products"
                element={
                  <Guarded perms={['bi.product.read']}>
                    <BiProducts />
                  </Guarded>
                }
              />
              <Route
                path="/bi/sku-mapping"
                element={
                  <Guarded perms={['bi.skumap.read']}>
                    <SkuMapping />
                  </Guarded>
                }
              />
              <Route
                path="/bi/inventory"
                element={
                  <Guarded perms={['bi.inventory.read']}>
                    <BiInventory />
                  </Guarded>
                }
              />
              {/* Declared AFTER /bi/inventory but matched exactly, so the nav's prefix-based
                  highlighting is the only thing that has to distinguish them. */}
              <Route
                path="/bi/inventory/search"
                element={
                  <Guarded perms={['bi.inventory.read']}>
                    <InventorySearch />
                  </Guarded>
                }
              />
              <Route
                path="/bi/purchasing"
                element={
                  <Guarded perms={['bi.purchasing.read']}>
                    <BiPurchasing />
                  </Guarded>
                }
              />
              <Route
                path="/rfid"
                element={
                  <Guarded perms={['rfid.dashboard.read']}>
                    <RfidDashboard />
                  </Guarded>
                }
              />
              <Route
                path="/rfid/receive"
                element={
                  <Guarded perms={['rfid.order.create']}>
                    <Receive />
                  </Guarded>
                }
              />
              <Route
                path="/rfid/lorry"
                element={
                  <Guarded perms={['rfid.order.create']}>
                    <Lorry />
                  </Guarded>
                }
              />
              <Route
                path="/rfid/orders"
                element={
                  <Guarded perms={['rfid.dashboard.read']}>
                    <RfidOrders />
                  </Guarded>
                }
              />
              <Route
                path="/rfid/postings"
                element={
                  <Guarded perms={['rfid.dashboard.read']}>
                    <RfidPostings />
                  </Guarded>
                }
              />
              <Route
                path="/rfid/stock-count"
                element={
                  <Guarded perms={['rfid.dashboard.read', 'bi.inventory.read']}>
                    <StockCount />
                  </Guarded>
                }
              />
              {/* One count's variance on its own page, so the Variance button can open a real tab
                  instead of expanding a panel below a long list of counts. */}
              <Route
                path="/rfid/stock-count/:billNo"
                element={
                  <Guarded perms={['rfid.dashboard.read', 'bi.inventory.read']}>
                    <StockCount />
                  </Guarded>
                }
              />
              <Route
                path="/rfid/epc-counts"
                element={
                  <Guarded perms={['rfid.epc.read']}>
                    <EpcCounts />
                  </Guarded>
                }
              />
              <Route
                path="/rfid/epc-counts/:id"
                element={
                  <Guarded perms={['rfid.epc.read']}>
                    <EpcCounts />
                  </Guarded>
                }
              />
              <Route
                path="/rfid/quality"
                element={
                  <Guarded perms={['rfid.epc.read']}>
                    <QualityInventory />
                  </Guarded>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <Guarded perms={['admin.user.read']}>
                    <AdminUsers />
                  </Guarded>
                }
              />
              <Route
                path="/admin/roles"
                element={
                  <Guarded perms={['admin.role.read']}>
                    <AdminRoles />
                  </Guarded>
                }
              />
              <Route
                path="/admin/audit"
                element={
                  <Guarded perms={['admin.audit.read']}>
                    <AdminAudit />
                  </Guarded>
                }
              />
              <Route
                path="/admin/health"
                element={
                  <Guarded perms={['rfid.job.read']}>
                    <SystemHealth />
                  </Guarded>
                }
              />
              <Route path="/account" element={<ChangePassword />} />
              <Route path="*" element={<Navigate to={home} replace />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </div>
  );
}

function Gate() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <p className="text-sm text-slate-400">Loading…</p>
      </div>
    );
  }
  return user ? <Shell /> : <Login />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        {/* A tab left open keeps running the bundle it loaded, however many times we deploy. */}
        <StaleBuild />
        <Gate />
      </BrowserRouter>
    </AuthProvider>
  );
}
